import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "next-i18next";
import { TabIdentityContext } from "components/Navigation/TabIdentityContext";
import { frontendRelease } from "utilities/release";
import { fetchBackendRelease, getBackendRelease } from "utilities/backendRelease";
import { buildSupportDetails, displayedRelease, versionLabel } from "utilities/supportDetails";
import { copyToClipboard } from "utilities/clipboard";
import { hubVersionLabel } from "utilities/hubVersionLabel";

/**
 * The Hub's version label + "Copy support details" (SCHED-658).
 *
 * Shows `OMCi v1.0.0 · Production · 30 Sep 2026`: version, release date and environment
 * from the deployment's env file (NEXT_PUBLIC_VERSION_CODE / _RELEASE_DATE / _ENVIRONMENT,
 * utilities/release.js); the environment falls back to the runtime OMC_ENV_TAG
 * (TabIdentityContext) when NEXT_PUBLIC_ENVIRONMENT is unset. When the running backend
 * (/api/get_version) reports a HIGHER version than the frontend, the label shows the backend's
 * version and release date instead — the portal's original rule, so a backend-only release is
 * visible without a frontend redeploy. The commit and build id in the copied details come
 * from the bundle (build-info.json); the copied text always lists both builds.
 * Clicking the label or the copy icon puts the support details on the
 * clipboard: application, the displayed version label, loaded frontend and launcher and running backend builds, environment,
 * port, page path (no query string), UTC time and browser/OS — never credentials,
 * cookies or personal details. The backend identity comes from /api/get_version
 * (fetched once per page); when it is not available the details still copy with
 * the backend marked "Unavailable".
 *
 * Feedback replaces the label: "Support details copied" only after the browser
 * confirmed the write, a retry message otherwise. No expanded panel, no separate
 * FE/BE display — those live in the copied text.
 *
 * Layout: text and glyph side by side (the glyph directly after the text, whatever the width
 * of the container). Props: `className` (or legacy `style`, a class string) for placement/typography;
 * `portName` — the port in view, for the copied details; `onTooltipEnter` /
 * `onTooltipLeave` — the sidebar's portal tooltip (falls back to a native title).
 */
const FEEDBACK_MS = { copied: 2500, failed: 5000 };
const APPLICATION = "OMCi Hub";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

/** Figma "copy" glyph (two stacked sheets), drawn in currentColor so it follows the theme. */
export function CopyIcon({ className }) {
    return (
        <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path
                d="M10 6H4.5C4.23478 6 3.98043 6.10536 3.79289 6.29289C3.60536 6.48043 3.5 6.73478 3.5 7V12.5C3.5 12.7652 3.60536 13.0196 3.79289 13.2071C3.98043 13.3946 4.23478 13.5 4.5 13.5H10C10.2652 13.5 10.5196 13.3946 10.7071 13.2071C10.8946 13.0196 11 12.7652 11 12.5V9.5V7C11 6.73478 10.8946 6.48043 10.7071 6.29289C10.5196 6.10536 10.2652 6 10 6Z"
                fill="currentColor"
                fillOpacity="0.7"
            />
            <path
                d="M5 4.5V3C5 2.73478 5.10536 2.48043 5.29289 2.29289C5.48043 2.10536 5.73478 2 6 2H11.5C11.7652 2 12.0196 2.10536 12.2071 2.29289C12.3946 2.48043 12.5 2.73478 12.5 3V8.5C12.5 8.76522 12.3946 9.01957 12.2071 9.20711C12.0196 9.39464 11.7652 9.5 11.5 9.5H10M4.5 6H10C10.2652 6 10.5196 6.10536 10.7071 6.29289C10.8946 6.48043 11 6.73478 11 7V12.5C11 12.7652 10.8946 13.0196 10.7071 13.2071C10.5196 13.3946 10.2652 13.5 10 13.5H4.5C4.23478 13.5 3.98043 13.3946 3.79289 13.2071C3.60536 13.0196 3.5 12.7652 3.5 12.5V7C3.5 6.73478 3.60536 6.48043 3.79289 6.29289C3.98043 6.10536 4.23478 6 4.5 6Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export default function AppVersion({ style, className, portName = null, onTooltipEnter, onTooltipLeave }) {
    const { t, i18n } = useTranslation("common");
    const { envName } = useContext(TabIdentityContext);
    const [backend, setBackend] = useState(() => getBackendRelease());
    const [status, setStatus] = useState("idle");
    const timer = useRef(null);

    useEffect(() => {
        let active = true;
        fetchBackendRelease().then((release) => {
            if (active && release) setBackend(release);
        });
        return () => {
            active = false;
            clearTimeout(timer.current);
        };
    }, []);

    // Pages that load no `common` namespace (the sign-in page) get react-i18next's not-ready
    // `t`, which returns the KEY and ignores defaultValue — treat that as missing.
    const translate = (key, fallback) => {
        const value = t(key, { defaultValue: fallback });
        return typeof value === "string" && value && value !== key ? value : fallback;
    };
    const frontend = frontendRelease();
    const environment = frontend.environment || envName;
    const shown = displayedRelease(frontend, backend); // backend wins only when its version is higher
    const label = versionLabel({
        version: shown.version,
        environment,
        releaseDate: shown.releaseDate,
        language: i18n && i18n.language,
        translate,
    });
    const copyLabel = translate("support_details.copy", "Copy support details");

    const copy = () => {
        const text = buildSupportDetails({
            application: APPLICATION,
            // The label as displayed, but in English like the rest of the copied text.
            displayedVersion: hubVersionLabel(envName, backend || getBackendRelease()),
            frontend,
            // The product switcher loaded on this page (omc-hub.js), when there is one.
            launcher: typeof window !== "undefined" && window.OMCHub ? window.OMCHub.version : null,
            backend: backend || getBackendRelease(),
            environment,
            port: portName,
            // The page path (+ fragment, which identifies the dashboard page) — never the query string.
            url: typeof window !== "undefined" ? window.location.pathname + window.location.hash : "",
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        });
        // The clipboard write starts inside the click (Safari); the outcome sets the feedback.
        copyToClipboard(text).then((ok) => {
            setStatus(ok ? "copied" : "failed");
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setStatus("idle"), FEEDBACK_MS[ok ? "copied" : "failed"]);
        });
        // A backend that was unavailable gets another chance for the next copy.
        if (!backend) fetchBackendRelease().then((release) => release && setBackend(release));
    };

    const message =
        status === "copied"
            ? translate("support_details.copied", "Support details copied")
            : status === "failed"
            ? translate("support_details.copy_failed", "Couldn't copy. Try again.")
            : label;

    return (
        <button
            type="button"
            onClick={copy}
            aria-label={copyLabel}
            title={onTooltipEnter ? undefined : copyLabel}
            data-status={status}
            data-test-id="app-version"
            onMouseEnter={onTooltipEnter ? (e) => onTooltipEnter(e, copyLabel, { force: true }) : undefined}
            onMouseLeave={onTooltipLeave}
            className={classNames(
                // The glyph sits right after the text (gap-1.5), never pushed to the far edge of a wide container.
                "group inline-flex max-w-full min-w-0 items-center justify-start gap-1.5 rounded-omc text-left cursor-pointer",
                className || style,
            )}
        >
            <span
                aria-live="polite"
                className={classNames("truncate", status === "copied" && "text-status-success", status === "failed" && "text-status-danger")}
            >
                {message}
            </span>
            <CopyIcon className="h-4 w-4 flex-shrink-0 text-icon-secondary group-hover:text-ink" />
        </button>
    );
}
